// src/components/resources/tools/generators/SvgAnimation/buildSvg.ts

/**
 * @fileoverview Self-contained animated-SVG builder for the SVG Animation
 * Generator tool.
 *
 * Every preset returns a COMPLETE, standalone `<svg>…</svg>` document string.
 * The animation lives entirely in an inline `<style>` block of CSS
 * `@keyframes` driving only GPU-compositable properties (`transform`,
 * `opacity`) plus the cheap `stroke-dashoffset` reveal — so it animates on its
 * own wherever the file lands (an `<img>`, inline, an `<object>`, a README, an
 * email, a design tool) with no JavaScript and no external assets.
 *
 * Design decisions (informed by 2025–2026 best-practice research):
 *  - **Internal CSS over SMIL.** Internal `<style>` `@keyframes` animate inside
 *    `<img>`-referenced SVGs in every current engine (Chrome, Firefox 51+,
 *    Safari) — the only thing blocked is the *host page's external* stylesheet.
 *    Choosing CSS (over SMIL) lets a single `@media (prefers-reduced-motion)`
 *    block disable EVERY preset; SMIL cannot be paused that way.
 *  - **Namespaced everything.** Class names, gradient/filter ids are prefixed
 *    with a per-render `uid`. Because an inline SVG's `<style>` is NOT scoped to
 *    the SVG (it leaks into the host document), namespacing is what lets a user
 *    paste several exported SVGs inline on one page without collisions — and is
 *    why the reduced-motion rule targets a `${uid}-a` marker class, never `*`.
 *  - **Reduced motion is honored in the file itself** via a baked
 *    `@media (prefers-reduced-motion: reduce)` rule. This fires when the SVG is
 *    embedded INLINE (or via `<object>`); per the W3C `<img>` sandbox a
 *    `prefers-reduced-motion` media query does NOT evaluate for an
 *    `<img>`-embedded SVG (only `width`/`height`/`prefers-color-scheme` do), so
 *    `<img>` embeds must be frozen at the host level (the preview does this —
 *    see `buildSvg`'s `render.freeze`) or swapped for a static fallback. The
 *    UI/FAQ surface this caveat.
 *
 * The tool's live preview renders the very same string via a
 * `data:image/svg+xml` `<img>`, so the preview is byte-for-byte the export.
 */

export type SvgPresetId =
  | 'blob'
  | 'waves'
  | 'line-draw'
  | 'pulse'
  | 'spinner'
  | 'dots'
  | 'orb'
  | 'mesh'
  | 'orbit'
  | 'bars'
  | 'check'
  | 'divider'
  | 'sparkle'
  | 'scroll'
  | 'radar'

export interface SvgPresetMeta {
  id: SvgPresetId
  label: string
  description: string
}

/** Ordered registry powering the preset gallery + the data-record featureList. */
export const SVG_PRESETS: readonly SvgPresetMeta[] = [
  {
    id: 'blob',
    label: 'Morphing Blob',
    description: 'Layered organic blobs that slowly counter-rotate and breathe.',
  },
  {
    id: 'mesh',
    label: 'Mesh Gradient',
    description: 'Soft, drifting color clouds — an animated mesh-gradient backdrop.',
  },
  {
    id: 'waves',
    label: 'Gradient Waves',
    description: 'Layered waves drifting at different speeds — great for hero backdrops.',
  },
  {
    id: 'orb',
    label: 'Glowing Orb',
    description: 'A soft, breathing orb whose gradient shifts between your colors.',
  },
  {
    id: 'line-draw',
    label: 'Line Draw',
    description: 'A self-drawing growth line, perfect for charts and reveals.',
  },
  {
    id: 'spinner',
    label: 'Gradient Spinner',
    description: 'A smooth gradient loading spinner with rounded caps.',
  },
  {
    id: 'orbit',
    label: 'Orbit',
    description: 'Satellites orbiting a core — a refined loading or hero accent.',
  },
  {
    id: 'pulse',
    label: 'Pulse Rings',
    description: 'Concentric radar rings that pulse outward and fade.',
  },
  {
    id: 'dots',
    label: 'Bouncing Dots',
    description: 'A classic three-dot “typing” loader with a staggered bounce.',
  },
  {
    id: 'bars',
    label: 'Equalizer',
    description: 'Audio-style bars that bounce in a staggered rhythm.',
  },
  {
    id: 'check',
    label: 'Success Check',
    description: 'A ring and checkmark that draw on — confirmations and success states.',
  },
  {
    id: 'divider',
    label: 'Wave Divider',
    description: 'A drifting wave line with a travelling sheen — section dividers.',
  },
  {
    id: 'sparkle',
    label: 'Sparkle',
    description: 'Twinkling stars that scale and fade — accents for CTAs and “new” flags.',
  },
  {
    id: 'scroll',
    label: 'Scroll Cue',
    description: 'A mouse outline with a dot that glides down — hero “scroll for more”.',
  },
  {
    id: 'radar',
    label: 'Radar Sweep',
    description: 'A rotating sweep over rings — scanning, processing, and “analyzing” states.',
  },
] as const

export interface SvgOptions {
  preset: SvgPresetId
  /** Primary colour (hex). */
  primary: string
  /** Secondary colour (hex). */
  secondary: string
  /** Background fill (hex) or `'transparent'`. */
  background: string
  /** Exported width/height in px (square). */
  size: number
  /** Animation cycle length in seconds. */
  speed: number
  /** Bake a `prefers-reduced-motion` rule that freezes the animation. */
  reducedMotion: boolean
  /** Overlay a subtle film-grain texture (feTurbulence). */
  grain: boolean
}

export const DEFAULT_SVG_OPTIONS: SvgOptions = {
  preset: 'blob',
  primary: '#6366F1',
  secondary: '#22D3EE',
  background: 'transparent',
  size: 320,
  speed: 6,
  reducedMotion: true,
  grain: false,
}

const VB = 240 // square viewBox edge — all preset geometry is authored against this
const C = VB / 2 // centre

/** Round to 2dp and drop trailing zeros to keep the markup compact. */
function f(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Smooth, closed blob path through `radii.length` points sampled evenly
 * around the centre, using a Catmull-Rom → cubic-Bézier conversion.
 */
function blobPath(radii: number[]): string {
  const n = radii.length
  const pts: Array<readonly [number, number]> = radii.map((r, i) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2
    return [C + Math.cos(a) * r, C + Math.sin(a) * r] as const
  })
  // Wrap-around accessor — the modulo keeps the index in range; the `?? [C, C]`
  // fallback is unreachable but satisfies `noUncheckedIndexedAccess` without a
  // non-null assertion.
  const at = (i: number): readonly [number, number] => pts[((i % n) + n) % n] ?? [C, C]
  const start = at(0)
  let d = `M ${f(start[0])} ${f(start[1])}`
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1)
    const p1 = at(i)
    const p2 = at(i + 1)
    const p3 = at(i + 2)
    const c1x = p1[0] + (p2[0] - p0[0]) / 6
    const c1y = p1[1] + (p2[1] - p0[1]) / 6
    const c2x = p2[0] - (p3[0] - p1[0]) / 6
    const c2y = p2[1] - (p3[1] - p1[1]) / 6
    d += ` C ${f(c1x)} ${f(c1y)}, ${f(c2x)} ${f(c2y)}, ${f(p2[0])} ${f(p2[1])}`
  }
  return `${d} Z`
}

/** A tiling horizontal wave path, closed down to the SVG floor for filling. */
function wavePath(baseY: number, amplitude: number, wavelength: number, width: number): string {
  const step = wavelength / 8
  let d = `M 0 ${f(baseY)}`
  for (let x = 0; x <= width; x += step) {
    const y = baseY + Math.sin((x / wavelength) * Math.PI * 2) * amplitude
    d += ` L ${f(x)} ${f(y)}`
  }
  d += ` L ${f(width)} ${VB} L 0 ${VB} Z`
  return d
}

const HEX_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
/**
 * Complete rgb()/hsl() grammars (comma-separated legacy form + space-separated
 * modern form, optional alpha). The previous character-class check accepted
 * malformed forms like `rgb(,)` or `hsl(deg)` — the field then read as valid
 * while the exported paint value rendered black/missing (`r3540509922`).
 * Building blocks: NUM (CSS <number>), NUM%/NUM (rgb channel / alpha),
 * NUM with an optional angle unit (hsl hue), NUM% (hsl saturation/lightness).
 */
const NUM = String.raw`[+-]?(?:\d+(?:\.\d+)?|\.\d+)`
const CHAN = `${NUM}%?`
const HUE = `${NUM}(?:deg|grad|rad|turn)?`
const PCT = `${NUM}%`
const RGB_RE = new RegExp(
  `^rgba?\\(\\s*(?:` +
    `${CHAN}\\s*,\\s*${CHAN}\\s*,\\s*${CHAN}(?:\\s*,\\s*${CHAN})?` + // legacy commas
    `|${CHAN}(?:\\s+${CHAN}){2}(?:\\s*/\\s*${CHAN})?` + // modern spaces
  `)\\s*\\)$`,
  'i',
)
const HSL_RE = new RegExp(
  `^hsla?\\(\\s*(?:` +
    `${HUE}\\s*,\\s*${PCT}\\s*,\\s*${PCT}(?:\\s*,\\s*${CHAN})?` + // legacy commas
    `|${HUE}(?:\\s+${PCT}){2}(?:\\s*/\\s*${CHAN})?` + // modern spaces
  `)\\s*\\)$`,
  'i',
)
/**
 * The CSS named colours (CSS Color Module Level 4 extended keywords +
 * `transparent`). A bare-word branch that accepted ANY alphabetic token let a
 * typo like `blu` through validation-as-valid while producing an invalid paint
 * value in the export (fills disappear / render browser-default) — the named
 * branch must accept only real colour keywords (`r3540274031`).
 */
const CSS_NAMED_COLORS: ReadonlySet<string> = new Set([
  'aliceblue', 'antiquewhite', 'aqua', 'aquamarine', 'azure', 'beige', 'bisque',
  'black', 'blanchedalmond', 'blue', 'blueviolet', 'brown', 'burlywood', 'cadetblue',
  'chartreuse', 'chocolate', 'coral', 'cornflowerblue', 'cornsilk', 'crimson',
  'cyan', 'darkblue', 'darkcyan', 'darkgoldenrod', 'darkgray', 'darkgreen', 'darkgrey',
  'darkkhaki', 'darkmagenta', 'darkolivegreen', 'darkorange', 'darkorchid', 'darkred',
  'darksalmon', 'darkseagreen', 'darkslateblue', 'darkslategray', 'darkslategrey',
  'darkturquoise', 'darkviolet', 'deeppink', 'deepskyblue', 'dimgray', 'dimgrey',
  'dodgerblue', 'firebrick', 'floralwhite', 'forestgreen', 'fuchsia', 'gainsboro',
  'ghostwhite', 'gold', 'goldenrod', 'gray', 'green', 'greenyellow', 'grey',
  'honeydew', 'hotpink', 'indianred', 'indigo', 'ivory', 'khaki', 'lavender',
  'lavenderblush', 'lawngreen', 'lemonchiffon', 'lightblue', 'lightcoral', 'lightcyan',
  'lightgoldenrodyellow', 'lightgray', 'lightgreen', 'lightgrey', 'lightpink',
  'lightsalmon', 'lightseagreen', 'lightskyblue', 'lightslategray', 'lightslategrey',
  'lightsteelblue', 'lightyellow', 'lime', 'limegreen', 'linen', 'magenta', 'maroon',
  'mediumaquamarine', 'mediumblue', 'mediumorchid', 'mediumpurple', 'mediumseagreen',
  'mediumslateblue', 'mediumspringgreen', 'mediumturquoise', 'mediumvioletred',
  'midnightblue', 'mintcream', 'mistyrose', 'moccasin', 'navajowhite', 'navy',
  'oldlace', 'olive', 'olivedrab', 'orange', 'orangered', 'orchid', 'palegoldenrod',
  'palegreen', 'paleturquoise', 'palevioletred', 'papayawhip', 'peachpuff', 'peru',
  'pink', 'plum', 'powderblue', 'purple', 'rebeccapurple', 'red', 'rosybrown',
  'royalblue', 'saddlebrown', 'salmon', 'sandybrown', 'seagreen', 'seashell',
  'sienna', 'silver', 'skyblue', 'slateblue', 'slategray', 'slategrey', 'snow',
  'springgreen', 'steelblue', 'tan', 'teal', 'thistle', 'tomato', 'transparent',
  'turquoise', 'violet', 'wheat', 'white', 'whitesmoke', 'yellow', 'yellowgreen',
])

/**
 * Normalize a user-supplied colour to a known-safe CSS colour, falling back to
 * `fallback` for anything else.
 *
 * This is the security boundary for the export: the tool emits markup the user
 * COPIES and INLINES into HTML/README/email, so an unvalidated value such as
 * `"/><script>…` would survive verbatim and execute once inlined (and a stray
 * `&`/`<`/`>` silently produces non-well-formed SVG). Restricting every
 * interpolated colour to hex / `rgb()` / `hsl()` / a bare CSS keyword keeps the
 * output free of `< > & "` — so there is nothing to escape downstream.
 */
function sanitizeColor(value: string, fallback: string): string {
  const v = (value ?? '').trim()
  if (isRenderableColor(v)) return v
  return fallback
}

/**
 * True when `value` (after trimming) is a colour the export engine will render
 * as-is — the exact acceptance rule `sanitizeColor` applies. Exported so the
 * UI's colour text inputs can validate with the SAME rule the engine uses,
 * keeping the field, the preview, and the export in sync (`r3408229080`).
 */
export function isRenderableColor(value: string): boolean {
  const v = (value ?? '').trim()
  return HEX_RE.test(v) || RGB_RE.test(v) || HSL_RE.test(v) || CSS_NAMED_COLORS.has(v.toLowerCase())
}

/**
 * Best-effort PURE conversion of a renderable colour to a normalized 6-digit
 * lowercase hex the native `<input type="color">` can represent (it accepts
 * ONLY `#rrggbb`). Handles hex (3/4/6/8 digit — alpha stripped), `rgb()`/
 * `rgba()` (number or percent channels), and `hsl()`/`hsla()`. Returns `null`
 * for values it can't derive (named colours, `transparent`) so the caller can
 * DECOUPLE the picker instead of letting the browser coerce the swatch to
 * black and overwrite the committed colour on the next interaction
 * (`r3540509929`). Deliberately DOM-free so SSR and hydration renders agree.
 */
export function colorToHex6(value: string): string | null {
  const v = (value ?? '').trim()
  if (HEX_RE.test(v)) {
    const rgb = parseHex(v)
    if (!rgb) return null
    return `#${rgb.map((n) => n.toString(16).padStart(2, '0')).join('')}`.toLowerCase()
  }
  const clamp255 = (n: number): number => Math.max(0, Math.min(255, Math.round(n)))
  if (RGB_RE.test(v)) {
    const body = v.slice(v.indexOf('(') + 1, v.lastIndexOf(')'))
    const parts = (body.includes(',') ? body.split(',') : (body.split('/')[0] ?? '').split(/\s+/))
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 3)
    if (parts.length !== 3) return null
    const rgb = parts.map((t) => (t.endsWith('%') ? clamp255(Number.parseFloat(t) * 2.55) : clamp255(Number.parseFloat(t))))
    if (rgb.some((n) => Number.isNaN(n))) return null
    return `#${rgb.map((n) => n.toString(16).padStart(2, '0')).join('')}`
  }
  if (HSL_RE.test(v)) {
    const body = v.slice(v.indexOf('(') + 1, v.lastIndexOf(')'))
    const parts = (body.includes(',') ? body.split(',') : (body.split('/')[0] ?? '').split(/\s+/))
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 3)
    if (parts.length !== 3) return null
    const hueRaw = parts[0] ?? ''
    const hueNum = Number.parseFloat(hueRaw)
    const hue = hueRaw.endsWith('turn')
      ? hueNum * 360
      : hueRaw.endsWith('grad')
        ? hueNum * 0.9
        : hueRaw.endsWith('rad') && !hueRaw.endsWith('grad')
          ? (hueNum * 180) / Math.PI
          : hueNum
    const sat = Number.parseFloat(parts[1] ?? '') / 100
    const light = Number.parseFloat(parts[2] ?? '') / 100
    if ([hue, sat, light].some((n) => Number.isNaN(n))) return null
    const h = ((hue % 360) + 360) % 360
    const sCl = Math.max(0, Math.min(1, sat))
    const lCl = Math.max(0, Math.min(1, light))
    const chroma = (1 - Math.abs(2 * lCl - 1)) * sCl
    const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = lCl - chroma / 2
    const [r1, g1, b1] =
      h < 60 ? [chroma, x, 0] : h < 120 ? [x, chroma, 0] : h < 180 ? [0, chroma, x] :
      h < 240 ? [0, x, chroma] : h < 300 ? [x, 0, chroma] : [chroma, 0, x]
    const rgb = [r1, g1, b1].map((c) => clamp255((c + m) * 255))
    return `#${rgb.map((n) => n.toString(16).padStart(2, '0')).join('')}`
  }
  return null
}

function parseHex(hex: string): [number, number, number] | null {
  let s = hex.replace('#', '')
  if (s.length === 3 || s.length === 4) s = s.slice(0, 3).split('').map((c) => c + c).join('')
  if (s.length === 8) s = s.slice(0, 6)
  if (s.length !== 6) return null
  const n = Number.parseInt(s, 16)
  if (Number.isNaN(n)) return null
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** A brighter blend of two hex colours — a luminous mid-stop that avoids the
 * muddy midpoint a 2-stop indigo→cyan ramp passes through. Returns null for
 * non-hex inputs (callers fall back to a 2-stop gradient). */
/** True for a hex colour that carries an alpha channel (#rgba or #rrggbbaa). */
function hasAlpha(hex: string): boolean {
  return /^#(?:[0-9a-fA-F]{4}|[0-9a-fA-F]{8})$/.test(hex)
}

function midColor(a: string, b: string): string | null {
  // A derived mid-stop drops the alpha channel, so for translucent endpoints it
  // would paint an opaque band between two transparent colours. Skip the midpoint
  // (fall back to a 2-stop ramp) whenever either endpoint has alpha.
  if (hasAlpha(a) || hasAlpha(b)) return null
  const pa = parseHex(a)
  const pb = parseHex(b)
  if (!pa || !pb) return null
  const ch = (i: number): string => {
    const v = Math.max(0, Math.min(255, Math.round(((pa[i] ?? 0) + (pb[i] ?? 0)) / 2 + 24)))
    return v.toString(16).padStart(2, '0')
  }
  return `#${ch(0)}${ch(1)}${ch(2)}`
}

function linearGradient(uid: string, primary: string, secondary: string): string {
  const mid = midColor(primary, secondary)
  const stops = mid
    ? `<stop offset="0%" stop-color="${primary}"/><stop offset="50%" stop-color="${mid}"/><stop offset="100%" stop-color="${secondary}"/>`
    : `<stop offset="0%" stop-color="${primary}"/><stop offset="100%" stop-color="${secondary}"/>`
  return `<linearGradient id="${uid}-g" x1="0" y1="0" x2="1" y2="1">${stops}</linearGradient>`
}

/**
 * A soft radial "glow" gradient (colour core → transparent edge). Filling a
 * circle with this gives a luminous halo with NO `feGaussianBlur` — so it can
 * sit behind a transform-animated shape without the per-frame filter
 * re-rasterization cost. This is the depth primitive the premium presets share.
 */
function glowGradient(uid: string, color: string): string {
  return (
    `<radialGradient id="${uid}-glow" cx="50%" cy="50%" r="50%">` +
    `<stop offset="0%" stop-color="${color}" stop-opacity="0.55"/>` +
    `<stop offset="55%" stop-color="${color}" stop-opacity="0.16"/>` +
    `<stop offset="100%" stop-color="${color}" stop-opacity="0"/>` +
    `</radialGradient>`
  )
}

function backgroundRect(background: string): string {
  if (background === 'transparent') return ''
  return `<rect width="${VB}" height="${VB}" rx="28" fill="${background}"/>`
}

/**
 * Build the preset-specific `<defs>` + `<style>` + body markup.
 *
 * Every element that carries a CSS `animation` ALSO carries the shared
 * `${uid}-a` marker class so the wrapper's reduced-motion rule can freeze it.
 */
function buildBody(uid: string, opts: SvgOptions): string {
  // Sanitize every user-supplied colour up front — see `sanitizeColor`. All
  // downstream interpolation is then guaranteed free of markup-breaking chars.
  const primary = sanitizeColor(opts.primary, DEFAULT_SVG_OPTIONS.primary)
  const secondary = sanitizeColor(opts.secondary, DEFAULT_SVG_OPTIONS.secondary)
  const background = sanitizeColor(opts.background, 'transparent')
  const { speed } = opts
  const a = `${uid}-a` // reduced-motion marker
  const bg = backgroundRect(background)
  const grad = linearGradient(uid, primary, secondary)

  switch (opts.preset) {
    case 'blob': {
      const p1 = blobPath([86, 66, 82, 90, 64, 78, 72, 88])
      const p2 = blobPath([80, 90, 70, 84, 92, 74, 82, 78])
      return (
        `<defs>${grad}${glowGradient(uid, secondary)}</defs>` +
        bg +
        `<style>` +
        // Gentle counter-oscillating wobble + asymmetric breathe reads as an
        // organic morph, not a rotating polygon. No 360° spin.
        `@keyframes ${uid}-wob{0%,100%{transform:rotate(-7deg)}50%{transform:rotate(7deg)}}` +
        `@keyframes ${uid}-wob2{0%,100%{transform:rotate(8deg) scale(1.03)}50%{transform:rotate(-6deg) scale(0.96)}}` +
        `@keyframes ${uid}-breathe{0%,100%{transform:scale(0.96)}50%{transform:scale(1.05)}}` +
        `@keyframes ${uid}-glow{0%,100%{opacity:0.5}50%{opacity:0.8}}` +
        `.${uid}-bg{transform-origin:${C}px ${C}px;animation:${uid}-breathe ${speed}s ease-in-out infinite}` +
        `.${uid}-b{transform-origin:${C}px ${C}px}` +
        `.${uid}-b1{animation:${uid}-wob ${speed}s ease-in-out infinite}` +
        `.${uid}-b2{animation:${uid}-wob2 ${f(speed * 1.3)}s ease-in-out infinite}` +
        `.${uid}-gl{transform-origin:${C}px ${C}px;animation:${uid}-glow ${speed}s ease-in-out infinite}` +
        `</style>` +
        // Static, filter-free glow halo (radial gradient) for depth.
        `<circle class="${uid}-gl ${a}" cx="${C}" cy="${C}" r="116" fill="url(#${uid}-glow)"/>` +
        `<g class="${uid}-bg ${a}">` +
        `<path class="${uid}-b ${uid}-b1 ${a}" d="${p1}" fill="url(#${uid}-g)"/>` +
        // Lighter additive overlay (low opacity) so the divergence glows softly
        // instead of producing muddy two-tone wedges.
        `<path class="${uid}-b ${uid}-b2 ${a}" d="${p2}" fill="${secondary}" opacity="0.28"/>` +
        // Soft specular highlight, top-left.
        `<ellipse cx="${C - 26}" cy="${C - 30}" rx="30" ry="20" fill="#ffffff" opacity="0.18"/>` +
        `</g>`
      )
    }

    case 'mesh': {
      // Soft colour clouds via radial-gradient fills (colour → transparent),
      // NOT feGaussianBlur — so the drifting `translate` animation stays
      // compositor-friendly with no per-frame filter re-rasterization.
      const blobs = [
        { c: primary, cx: 80, cy: 78, r: 128, k: 'm1' },
        { c: secondary, cx: 166, cy: 84, r: 118, k: 'm2' },
        { c: secondary, cx: 74, cy: 170, r: 116, k: 'm3' },
        { c: primary, cx: 172, cy: 168, r: 126, k: 'm4' },
      ]
      const defsGrads = blobs
        .map(
          (b) =>
            `<radialGradient id="${uid}-${b.k}g" cx="50%" cy="50%" r="50%">` +
            `<stop offset="0%" stop-color="${b.c}" stop-opacity="0.95"/>` +
            `<stop offset="100%" stop-color="${b.c}" stop-opacity="0"/></radialGradient>`,
        )
        .join('')
      const moves =
        `@keyframes ${uid}-m1{0%,100%{transform:translate(0,0)}50%{transform:translate(34px,26px)}}` +
        `@keyframes ${uid}-m2{0%,100%{transform:translate(0,0)}50%{transform:translate(-30px,30px)}}` +
        `@keyframes ${uid}-m3{0%,100%{transform:translate(0,0)}50%{transform:translate(28px,-30px)}}` +
        `@keyframes ${uid}-m4{0%,100%{transform:translate(0,0)}50%{transform:translate(-32px,-26px)}}`
      const rules = blobs
        .map((b, i) => `.${uid}-${b.k}{animation:${uid}-${b.k} ${f(speed * (1.4 + i * 0.25))}s ease-in-out infinite}`)
        .join('')
      const circles = blobs
        .map((b) => `<circle cx="${b.cx}" cy="${b.cy}" r="${b.r}" fill="url(#${uid}-${b.k}g)" class="${uid}-${b.k} ${a}"/>`)
        .join('')
      return (
        `<defs>` +
        `<clipPath id="${uid}-clip"><rect width="${VB}" height="${VB}" rx="28"/></clipPath>` +
        defsGrads +
        `</defs>` +
        // Honor the background control: only paint a canvas for a solid
        // background. On `transparent` the colour clouds float on transparent
        // (usable as a hero overlay) rather than a forced dark rect.
        bg +
        `<style>${moves}${rules}</style>` +
        `<g clip-path="url(#${uid}-clip)">${circles}</g>`
      )
    }

    case 'waves': {
      // Raised baselines + larger amplitudes so the waves fill more of the frame.
      const w1 = wavePath(96, 20, 120, VB * 2)
      const w2 = wavePath(128, 16, 96, VB * 2)
      const w3 = wavePath(158, 12, 72, VB * 2)
      return (
        `<defs>${grad}` +
        `<clipPath id="${uid}-clip"><rect width="${VB}" height="${VB}" rx="28"/></clipPath></defs>` +
        bg +
        // Each layer travels EXACTLY one of its own wavelengths (120/96/72px) so
        // the sine pattern is at the same phase at loop reset — no per-cycle jump.
        `<style>` +
        `@keyframes ${uid}-d1{from{transform:translateX(0)}to{transform:translateX(-120px)}}` +
        `@keyframes ${uid}-d2{from{transform:translateX(0)}to{transform:translateX(-96px)}}` +
        `@keyframes ${uid}-d3{from{transform:translateX(0)}to{transform:translateX(-72px)}}` +
        `.${uid}-w1{animation:${uid}-d1 ${speed}s linear infinite}` +
        `.${uid}-w2{animation:${uid}-d2 ${f(speed * 0.7)}s linear infinite}` +
        `.${uid}-w3{animation:${uid}-d3 ${f(speed * 1.4)}s linear infinite}` +
        `</style>` +
        `<g clip-path="url(#${uid}-clip)">` +
        `<path class="${uid}-w1 ${a}" d="${w1}" fill="url(#${uid}-g)" opacity="0.35"/>` +
        `<path class="${uid}-w2 ${a}" d="${w2}" fill="url(#${uid}-g)" opacity="0.55"/>` +
        `<path class="${uid}-w3 ${a}" d="${w3}" fill="url(#${uid}-g)" opacity="0.9"/>` +
        `</g>`
      )
    }

    case 'line-draw': {
      // A proper chart reveal: line draws on, area fills up beneath it, end
      // marker lands — matching the quality of the Motion Lab sparkline.
      const line = `M 20 186 L 72 138 L 112 158 L 158 96 L 220 52`
      const area = `${line} L 220 200 L 20 200 Z`
      return (
        `<defs>${grad}${glowGradient(uid, secondary)}` +
        `<linearGradient id="${uid}-area" x1="0" y1="0" x2="0" y2="1">` +
        `<stop offset="0%" stop-color="${primary}" stop-opacity="0.32"/>` +
        `<stop offset="100%" stop-color="${primary}" stop-opacity="0"/></linearGradient></defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-draw{0%{stroke-dashoffset:1}55%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}` +
        `@keyframes ${uid}-area{0%,30%{opacity:0}70%,100%{opacity:1}}` +
        `@keyframes ${uid}-dot{0%,48%{opacity:0;transform:scale(0)}64%{opacity:1;transform:scale(1.25)}80%,100%{opacity:1;transform:scale(1)}}` +
        `.${uid}-p{stroke-dasharray:1;animation:${uid}-draw ${speed}s ease-in-out infinite alternate}` +
        `.${uid}-ar{animation:${uid}-area ${speed}s ease-in-out infinite alternate}` +
        `.${uid}-tip{transform-box:fill-box;transform-origin:center;animation:${uid}-dot ${speed}s ease-in-out infinite alternate}` +
        `</style>` +
        `<path class="${uid}-ar ${a}" d="${area}" fill="url(#${uid}-area)"/>` +
        `<path class="${uid}-p ${a}" pathLength="1" d="${line}" fill="none" stroke="url(#${uid}-g)" ` +
        `stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>` +
        `<circle cx="220" cy="52" r="16" fill="url(#${uid}-glow)"/>` +
        `<circle class="${uid}-tip ${a}" cx="220" cy="52" r="6.5" fill="${secondary}"/>`
      )
    }

    case 'pulse': {
      const rings = [0, 1, 2]
        .map(
          (i) =>
            `<circle cx="${C}" cy="${C}" r="100" fill="none" stroke="url(#${uid}-g)" ` +
            `stroke-width="6" class="${uid}-ring ${a}" style="animation-delay:${f(-(speed / 3) * i)}s"/>`,
        )
        .join('')
      return (
        `<defs>${grad}</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-pulse{0%{transform:scale(0.12);opacity:0.9}100%{transform:scale(1);opacity:0}}` +
        `.${uid}-ring{transform-origin:${C}px ${C}px;animation:${uid}-pulse ${speed}s cubic-bezier(0.2,0.6,0.2,1) infinite}` +
        `</style>` +
        rings +
        `<circle cx="${C}" cy="${C}" r="14" fill="${primary}"/>`
      )
    }

    case 'spinner': {
      return (
        `<defs>${grad}</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-spin{to{transform:rotate(360deg)}}` +
        `.${uid}-s{transform-origin:${C}px ${C}px;animation:${uid}-spin ${speed}s linear infinite}` +
        `</style>` +
        `<circle cx="${C}" cy="${C}" r="84" fill="none" stroke="${primary}" stroke-width="16" opacity="0.15"/>` +
        `<circle class="${uid}-s ${a}" cx="${C}" cy="${C}" r="84" fill="none" stroke="url(#${uid}-g)" ` +
        `stroke-width="16" stroke-linecap="round" stroke-dasharray="190 528"/>`
      )
    }

    case 'orbit': {
      // Geometry scaled to fill the frame; rings lifted from near-invisible; a
      // glowing core gives a focal point.
      const ring = (r: number): string =>
        `<circle cx="${C}" cy="${C}" r="${r}" fill="none" stroke="${primary}" stroke-width="2.5" opacity="0.32"/>`
      return (
        `<defs>${grad}${glowGradient(uid, primary)}</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-spin{to{transform:rotate(360deg)}}` +
        `@keyframes ${uid}-spinr{to{transform:rotate(-360deg)}}` +
        `.${uid}-o1{transform-origin:${C}px ${C}px;animation:${uid}-spin ${speed}s linear infinite}` +
        `.${uid}-o2{transform-origin:${C}px ${C}px;animation:${uid}-spinr ${f(speed * 1.6)}s linear infinite}` +
        `.${uid}-o3{transform-origin:${C}px ${C}px;animation:${uid}-spin ${f(speed * 2.2)}s linear infinite}` +
        `</style>` +
        ring(104) +
        ring(66) +
        `<g class="${uid}-o1 ${a}"><circle cx="${C}" cy="${C - 104}" r="13" fill="url(#${uid}-g)"/></g>` +
        `<g class="${uid}-o2 ${a}"><circle cx="${C + 66}" cy="${C}" r="9.5" fill="${secondary}"/></g>` +
        `<g class="${uid}-o3 ${a}"><circle cx="${C - 66}" cy="${C}" r="7" fill="${primary}"/></g>` +
        `<circle cx="${C}" cy="${C}" r="40" fill="url(#${uid}-glow)"/>` +
        `<circle cx="${C}" cy="${C}" r="16" fill="url(#${uid}-g)"/>`
      )
    }

    case 'dots': {
      const dots = [0, 1, 2]
        .map(
          (i) =>
            `<circle cx="${f(C - 56 + i * 56)}" cy="${C}" r="20" fill="url(#${uid}-g)" ` +
            `class="${uid}-dot ${a}" style="animation-delay:${f(-(speed / 6) * i)}s"/>`,
        )
        .join('')
      return (
        `<defs>${grad}${glowGradient(uid, primary)}</defs>` +
        bg +
        `<style>` +
        // Bigger travel + a touch of scale so the bounce reads clearly at small sizes.
        `@keyframes ${uid}-bounce{0%,70%,100%{transform:translateY(0) scale(0.92);opacity:0.5}35%{transform:translateY(-48px) scale(1.08);opacity:1}}` +
        `.${uid}-dot{transform-box:fill-box;transform-origin:center;animation:${uid}-bounce ${speed}s ease-in-out infinite}` +
        `</style>` +
        `<circle cx="${C}" cy="${C}" r="70" fill="url(#${uid}-glow)"/>` +
        dots
      )
    }

    case 'bars': {
      const count = 5
      const bw = 22
      const gap = 14
      const totalW = count * bw + (count - 1) * gap
      const x0 = C - totalW / 2
      // Per-bar phase + duration variance (fixed pseudo-random) so it reads as
      // an organic equalizer rather than a mechanical symmetric V.
      const phase = [0, 0.55, 0.2, 0.78, 0.36]
      const durMul = [1, 0.82, 1.18, 0.9, 1.06]
      const bars = Array.from({ length: count })
        .map((_, i) => {
          const x = x0 + i * (bw + gap)
          return (
            `<rect x="${f(x)}" y="64" width="${bw}" height="112" rx="11" fill="url(#${uid}-g)" ` +
            `class="${uid}-bar ${a}" style="animation-delay:${f(-((phase[i] ?? 0) * speed))}s;animation-duration:${f((durMul[i] ?? 1) * speed)}s"/>`
          )
        })
        .join('')
      return (
        `<defs>${grad}${glowGradient(uid, primary)}</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-eq{0%,100%{transform:scaleY(0.26)}50%{transform:scaleY(1)}}` +
        `.${uid}-bar{transform-box:fill-box;transform-origin:center bottom;animation:${uid}-eq ${speed}s ease-in-out infinite}` +
        `</style>` +
        `<circle cx="${C}" cy="${C + 12}" r="84" fill="url(#${uid}-glow)"/>` +
        bars
      )
    }

    case 'orb': {
      return (
        `<defs>` +
        `<radialGradient id="${uid}-g" cx="40%" cy="35%" r="70%">` +
        `<stop offset="0%" stop-color="${primary}"/><stop offset="100%" stop-color="${secondary}"/>` +
        `</radialGradient>` +
        `<radialGradient id="${uid}-g2" cx="40%" cy="35%" r="70%">` +
        `<stop offset="0%" stop-color="${secondary}"/><stop offset="100%" stop-color="${primary}"/>` +
        `</radialGradient>` +
        glowGradient(uid, secondary) +
        `</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-breathe{0%,100%{transform:scale(0.92)}50%{transform:scale(1.05)}}` +
        `@keyframes ${uid}-fade{0%,100%{opacity:0}50%{opacity:1}}` +
        `@keyframes ${uid}-glow{0%,100%{opacity:0.5}50%{opacity:0.85}}` +
        `.${uid}-o{transform-origin:${C}px ${C}px;animation:${uid}-breathe ${speed}s ease-in-out infinite}` +
        `.${uid}-f{animation:${uid}-fade ${speed}s ease-in-out infinite}` +
        `.${uid}-gl{transform-origin:${C}px ${C}px;animation:${uid}-glow ${speed}s ease-in-out infinite}` +
        `</style>` +
        // Glow halo is a STATIC filter-free radial gradient outside the breathing
        // group — no per-frame feGaussianBlur (only opacity, which is cheap).
        `<circle class="${uid}-gl ${a}" cx="${C}" cy="${C}" r="116" fill="url(#${uid}-glow)"/>` +
        `<g class="${uid}-o ${a}">` +
        `<circle cx="${C}" cy="${C}" r="80" fill="url(#${uid}-g)"/>` +
        `<circle cx="${C}" cy="${C}" r="80" fill="url(#${uid}-g2)" class="${uid}-f ${a}"/>` +
        `<circle cx="100" cy="96" r="24" fill="#ffffff" opacity="0.25"/>` +
        `</g>`
      )
    }

    case 'check': {
      const check = `M 92 122 L 112 144 L 152 96`
      return (
        `<defs>${grad}${glowGradient(uid, secondary)}</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-ring{0%{stroke-dashoffset:1}55%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}` +
        `@keyframes ${uid}-check{0%,45%{stroke-dashoffset:1}80%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}` +
        `@keyframes ${uid}-glow{0%,40%{opacity:0}75%,100%{opacity:0.7}}` +
        `.${uid}-ring{stroke-dasharray:1;animation:${uid}-ring ${speed}s ease-in-out infinite alternate}` +
        `.${uid}-chk{stroke-dasharray:1;animation:${uid}-check ${speed}s ease-in-out infinite alternate}` +
        `.${uid}-gl{animation:${uid}-glow ${speed}s ease-in-out infinite alternate}` +
        `</style>` +
        `<circle class="${uid}-gl ${a}" cx="${C}" cy="${C}" r="96" fill="url(#${uid}-glow)"/>` +
        `<circle cx="${C}" cy="${C}" r="74" fill="none" stroke="${primary}" stroke-width="8" opacity="0.14"/>` +
        `<circle class="${uid}-ring ${a}" pathLength="1" cx="${C}" cy="${C}" r="74" fill="none" ` +
        `stroke="url(#${uid}-g)" stroke-width="8" stroke-linecap="round" transform="rotate(-90 ${C} ${C})"/>` +
        `<path class="${uid}-chk ${a}" pathLength="1" d="${check}" fill="none" stroke="url(#${uid}-g)" ` +
        `stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>`
      )
    }

    case 'divider': {
      // A centred wavy line with a travelling sheen — drifts horizontally.
      const wave = (y: number): string => {
        const wl = 120
        let d = `M -20 ${y}`
        for (let x = -20; x <= VB * 2; x += wl / 8) d += ` L ${f(x)} ${f(y + Math.sin((x / wl) * Math.PI * 2) * 12)}`
        return d
      }
      return (
        `<defs>${grad}${glowGradient(uid, secondary)}` +
        `<clipPath id="${uid}-clip"><rect width="${VB}" height="${VB}" rx="28"/></clipPath></defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-drift{from{transform:translateX(0)}to{transform:translateX(-120px)}}` +
        // The sheen travels ALONG the path via stroke-dashoffset (the same
        // technique the line-draw preset uses), not via translateX: a
        // translated copy of a periodic wave only re-aligns at whole-wavelength
        // offsets, so a transform sweep reads as a second, detached wave for
        // most of its cycle. A dash window stays ON the wave at every instant.
        // One full pattern period (90 + 480 = 570, ≈ the path length) per
        // cycle keeps the loop seamless with minimal dead time between sweeps.
        `@keyframes ${uid}-sheen{from{stroke-dashoffset:0}to{stroke-dashoffset:570px}}` +
        `.${uid}-w{animation:${uid}-drift ${speed}s linear infinite}` +
        `.${uid}-sh{animation:${uid}-sheen ${f(speed * 1.4)}s ease-in-out infinite}` +
        `</style>` +
        `<g clip-path="url(#${uid}-clip)">` +
        `<circle cx="${C}" cy="${C}" r="92" fill="url(#${uid}-glow)" opacity="0.5"/>` +
        `<path class="${uid}-w ${a}" d="${wave(C)}" fill="none" stroke="url(#${uid}-g)" stroke-width="7" stroke-linecap="round"/>` +
        // The sheen must BOTH drift with the base wave AND run its own sweep.
        // Both effects previously set the `animation` shorthand on ONE element,
        // so only the later rule applied and the sheen lost the shared drift
        // (`r3540224146`) — nest instead: the group carries the shared drift
        // (transform), the inner path carries the dash sweep (stroke-dashoffset),
        // and the two animate independent properties.
        `<g class="${uid}-w ${a}"><path class="${uid}-sh ${a}" d="${wave(C)}" fill="none" stroke="#fff" stroke-opacity="0.8" stroke-width="7" stroke-linecap="round" stroke-dasharray="90 480"/></g>` +
        `</g>`
      )
    }

    case 'sparkle': {
      // 4-point stars that twinkle (scale + opacity) on staggered timers.
      const star = (cx: number, cy: number, r: number): string =>
        `M ${cx} ${cy - r} C ${cx + r * 0.12} ${cy - r * 0.12} ${cx + r * 0.12} ${cy - r * 0.12} ${cx + r} ${cy} ` +
        `C ${cx + r * 0.12} ${cy + r * 0.12} ${cx + r * 0.12} ${cy + r * 0.12} ${cx} ${cy + r} ` +
        `C ${cx - r * 0.12} ${cy + r * 0.12} ${cx - r * 0.12} ${cy + r * 0.12} ${cx - r} ${cy} ` +
        `C ${cx - r * 0.12} ${cy - r * 0.12} ${cx - r * 0.12} ${cy - r * 0.12} ${cx} ${cy - r} Z`
      const stars = [
        { cx: 120, cy: 96, r: 46, k: 0 },
        { cx: 72, cy: 150, r: 26, k: 1 },
        { cx: 170, cy: 158, r: 32, k: 2 },
      ]
      const body = stars
        .map(
          (s) =>
            `<path d="${star(s.cx, s.cy, s.r)}" fill="url(#${uid}-g)" class="${uid}-tw ${a}" ` +
            `style="transform-box:fill-box;transform-origin:center;animation-delay:${f(-(speed / 3) * s.k)}s"/>`,
        )
        .join('')
      return (
        `<defs>${grad}${glowGradient(uid, secondary)}</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-tw{0%,100%{transform:scale(0.4);opacity:0.25}50%{transform:scale(1);opacity:1}}` +
        `.${uid}-tw{animation:${uid}-tw ${speed}s ease-in-out infinite}` +
        `</style>` +
        `<circle cx="120" cy="110" r="90" fill="url(#${uid}-glow)" opacity="0.6"/>` +
        body
      )
    }

    case 'scroll': {
      return (
        `<defs>${grad}${glowGradient(uid, secondary)}</defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-scroll{0%{transform:translateY(0);opacity:0}25%{opacity:1}70%{transform:translateY(34px);opacity:0}100%{opacity:0}}` +
        `.${uid}-d{transform-box:fill-box;animation:${uid}-scroll ${speed}s ease-in-out infinite}` +
        `</style>` +
        `<circle cx="${C}" cy="${C}" r="86" fill="url(#${uid}-glow)" opacity="0.5"/>` +
        `<rect x="98" y="74" width="44" height="92" rx="22" fill="none" stroke="url(#${uid}-g)" stroke-width="6"/>` +
        `<circle class="${uid}-d ${a}" cx="${C}" cy="98" r="7" fill="url(#${uid}-g)"/>`
      )
    }

    case 'radar': {
      // Rotating sweep wedge (gradient → transparent) over faint rings.
      return (
        `<defs>${grad}${glowGradient(uid, primary)}` +
        `<linearGradient id="${uid}-sweep" x1="0" y1="0" x2="1" y2="0">` +
        `<stop offset="0%" stop-color="${secondary}" stop-opacity="0.7"/>` +
        `<stop offset="100%" stop-color="${secondary}" stop-opacity="0"/></linearGradient></defs>` +
        bg +
        `<style>` +
        `@keyframes ${uid}-spin{to{transform:rotate(360deg)}}` +
        `.${uid}-sw{transform-origin:${C}px ${C}px;animation:${uid}-spin ${speed}s linear infinite}` +
        `</style>` +
        `<circle cx="${C}" cy="${C}" r="64" fill="url(#${uid}-glow)"/>` +
        `<circle cx="${C}" cy="${C}" r="96" fill="none" stroke="${primary}" stroke-width="2" opacity="0.25"/>` +
        `<circle cx="${C}" cy="${C}" r="60" fill="none" stroke="${primary}" stroke-width="2" opacity="0.25"/>` +
        `<line x1="24" y1="${C}" x2="216" y2="${C}" stroke="${primary}" stroke-width="1.5" opacity="0.18"/>` +
        `<line x1="${C}" y1="24" x2="${C}" y2="216" stroke="${primary}" stroke-width="1.5" opacity="0.18"/>` +
        `<path class="${uid}-sw ${a}" d="M ${C} ${C} L ${C} 18 A 102 102 0 0 1 ${f(C + 102 * Math.sin(Math.PI / 3))} ${f(C - 102 * Math.cos(Math.PI / 3))} Z" fill="url(#${uid}-sweep)"/>` +
        `<circle cx="${C}" cy="${C}" r="9" fill="url(#${uid}-g)"/>`
      )
    }

    default: {
      // Exhaustiveness guard — a new preset id must be handled above.
      const _exhaustive: never = opts.preset
      return _exhaustive
    }
  }
}

/** A subtle, desaturated film-grain overlay (feTurbulence). */
function grainOverlay(uid: string): string {
  return (
    `<defs><filter id="${uid}-grain"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/>` +
    `<feColorMatrix type="saturate" values="0"/></filter></defs>` +
    `<rect width="${VB}" height="${VB}" rx="28" filter="url(#${uid}-grain)" opacity="0.12" style="mix-blend-mode:overlay" pointer-events="none"/>`
  )
}

/**
 * Build a complete, standalone animated `<svg>` document string for the given
 * options. `uid` namespaces all ids/classes so multiple exported SVGs can
 * coexist inline on one page.
 *
 * @param render.freeze When true, bake an UNCONDITIONAL freeze rule so the
 *   SVG renders a MEANINGFUL static frame: `animation:none` (the authored
 *   geometry — for draw-style presets that is the completed drawing) for most
 *   presets, or `animation-play-state:paused` at t=0 for the presets in
 *   FREEZE_AT_T0_PRESETS whose authored state is not representative (pulse:
 *   three identical full-size rings would superimpose). Used by the tool's
 *   `<img>` preview when
 *   the viewer prefers reduced motion — because, per the W3C `<img>` sandbox,
 *   a `prefers-reduced-motion` *media query* inside the file does NOT evaluate
 *   in `<img>` context (only `width`/`height`/`prefers-color-scheme` do), so the
 *   file can't self-freeze there; the host must freeze it.
 */
/**
 * Presets whose AUTHORED geometry is NOT a representative frozen frame, so
 * they freeze at t=0 via `animation-play-state:paused` (the per-ring negative
 * delays yield the staggered first frame). Everything else freezes to its
 * AUTHORED state via `animation:none`: for draw-style presets (line-draw,
 * check) the non-animated dashoffset/opacity IS the completed drawing, and for
 * the rest it's the composition the preset was designed around — whereas
 * pausing those at t=0 yields a blank asset (line-draw starts fully undrawn,
 * check's ring/mark/glow are all hidden at 0%, scroll's wheel dot is
 * opacity:0). Verified by rendering all fifteen presets frozen
 * (`r3541215186`; the pulse case is `r3540274029`).
 */
const FREEZE_AT_T0_PRESETS: ReadonlySet<SvgPresetId> = new Set(['pulse'])

/** The freeze rule for a preset — see FREEZE_AT_T0_PRESETS. */
function freezeRule(uid: string, preset: SvgPresetId): string {
  return FREEZE_AT_T0_PRESETS.has(preset)
    ? `.${uid}-a{animation-play-state:paused!important}`
    : `.${uid}-a{animation:none!important}`
}

export function buildSvg(opts: SvgOptions, uid = 'asvg', render?: { freeze?: boolean }): string {
  const safeUid = uid.replace(/[^a-zA-Z0-9_-]/g, '') || 'asvg'
  const body = buildBody(safeUid, opts)
  // The grain overlay paints a full-viewBox filtered rect, so on a TRANSPARENT
  // background it would stamp a visible noisy square over the empty canvas and
  // break the transparent-overlay promise. Grain needs a surface — only emit it
  // when a background is actually painted. Key the gate off the SAME sanitized
  // value the canvas rect uses (`buildBody`/`backgroundRect`): an invalid
  // background string sanitizes to `transparent` (no rect painted), so gating
  // on the raw value would export grain over an empty canvas (`r3540224140`).
  const paintedBackground = sanitizeColor(opts.background, 'transparent')
  const grain = opts.grain && paintedBackground !== 'transparent' ? grainOverlay(safeUid) : ''
  // Unconditional freeze for host-controlled reduced motion (see @param).
  if (render?.freeze) {
    const preset = SVG_PRESETS.find((p) => p.id === opts.preset)
    const title = `AdSights animated SVG — ${preset?.label ?? opts.preset}`
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB} ${VB}" ` +
      `width="${opts.size}" height="${opts.size}" role="img" aria-label="${title}">` +
      `<title>${title}</title>${body}${grain}` +
      `<style>${freezeRule(safeUid, opts.preset)}</style></svg>`
    )
  }
  // Reduced-motion rule, scoped to this SVG's animated nodes via the marker
  // class so it never reaches the host document when the SVG is inlined.
  // NOTE: this media query only fires when the SVG is INLINE (or <object>);
  // it is ignored in `<img>` context — a browser-level sandbox limitation, not
  // a bug in this output. The UI surfaces this caveat.
  const reduced = opts.reducedMotion
    // Same per-preset freeze semantics as the static export (freezeRule), so
    // an inline reduced-motion embed shows the SAME meaningful frame the
    // downloaded static file shows.
    ? `<style>@media (prefers-reduced-motion: reduce){${freezeRule(safeUid, opts.preset)}}</style>`
    : ''
  const preset = SVG_PRESETS.find((p) => p.id === opts.preset)
  const title = `AdSights animated SVG — ${preset?.label ?? opts.preset}`
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB} ${VB}" ` +
    `width="${opts.size}" height="${opts.size}" role="img" aria-label="${title}">` +
    `<title>${title}</title>` +
    body +
    grain +
    reduced +
    `</svg>`
  )
}

/** Encode an SVG document string as a `data:` URI suitable for `<img src>`. */
export function svgToDataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/**
 * Encode the SVG as a copy-paste-ready CSS `background-image` snippet.
 *
 * In a CSS `background-image` context (just like an `<img>`) the SVG's own
 * internal `@media (prefers-reduced-motion)` rule does NOT evaluate, so a
 * reduced-motion visitor would still see the animation. When a `frozenSvg`
 * (the static first frame) is supplied, we emit a HOST-level
 * `@media (prefers-reduced-motion: reduce)` override that swaps in the frozen
 * data URI — that media query lives in the host stylesheet, so it DOES fire —
 * honoring the same baked-in reduced-motion promise the inline/`.svg` exports
 * make. The snippet is a complete rule (with a placeholder selector) rather
 * than a bare declaration list, since `@media` can't live inside a declaration
 * block.
 */
export function svgToCssBackground(svg: string, frozenSvg?: string, className = 'adsights-animation'): string {
  // The selector is namespaced per export (callers pass the export uid) so two
  // CSS-background snippets pasted on one page don't share `.adsights-animation`
  // — otherwise the later rule (incl. its reduced-motion override) would clobber
  // the earlier one, mirroring the per-export id namespacing of the SVG itself.
  const cls = className.replace(/[^a-zA-Z0-9_-]/g, '') || 'adsights-animation'
  const base =
    `.${cls} {\n` +
    `  background-image: url("${svgToDataUri(svg)}");\n` +
    `  background-size: cover;\n` +
    `  background-repeat: no-repeat;\n` +
    `}`
  if (!frozenSvg) return base
  return (
    `${base}\n` +
    `@media (prefers-reduced-motion: reduce) {\n` +
    `  .${cls} {\n` +
    `    background-image: url("${svgToDataUri(frozenSvg)}");\n` +
    `  }\n` +
    `}`
  )
}

/**
 * Build the complete "Copy CSS" export for a set of options.
 *
 * Owns the reduced-motion decision so the CSS export honors the same
 * "Bake reduced-motion support" toggle as the SVG/download exports
 * (`r3544590422`): the host-level `@media (prefers-reduced-motion: reduce)`
 * override (see `svgToCssBackground`) is emitted only when
 * `opts.reducedMotion` is on — with the toggle off, the snippet is a single
 * animated rule, matching a downloaded `.svg` that carries no baked media
 * query. The frozen variant reuses the static export's `-static` uid suffix
 * so its ids stay disjoint from the animated markup's.
 */
export function buildCssExport(opts: SvgOptions, exportUid: string): string {
  return svgToCssBackground(
    buildSvg(opts, exportUid),
    opts.reducedMotion ? buildSvg(opts, `${exportUid}-static`, { freeze: true }) : undefined,
    // Per-export selector so two pasted CSS snippets don't collide.
    `adsights-${exportUid}`,
  )
}
